<template>
  <div class="password-strength">
    <slot />
    <div v-if="password" class="password-strength__bar-wrap" aria-hidden="true">
      <div
        class="password-strength__bar"
        :class="`password-strength__bar--${barLevel}`"
        :style="{ width: barWidth }"
      />
    </div>
    <p v-if="password && label" class="password-strength__label">{{ label }}</p>
    <ul v-if="password && showHints && pendingErrors.length" class="password-strength__hints">
      <li v-for="hint in pendingErrors" :key="hint">{{ hint }}</li>
    </ul>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import {
  passwordPolicyErrors,
  passwordStrengthLabel,
  passwordStrengthScore,
} from '@/utils/passwordPolicy'

const props = defineProps({
  password: { type: String, default: '' },
  showHints: { type: Boolean, default: true },
})

const score = computed(() => passwordStrengthScore(props.password))
const label = computed(() => (props.password ? passwordStrengthLabel(score.value) : ''))
const pendingErrors = computed(() => passwordPolicyErrors(props.password))

const barLevel = computed(() => {
  const s = score.value
  if (s <= 1) return 'weak'
  if (s <= 3) return 'mid'
  if (s === 4) return 'good'
  return 'strong'
})

const barWidth = computed(() => `${Math.max(12, (score.value / 5) * 100)}%`)
</script>

<style scoped>
.password-strength__bar-wrap {
  height: 4px;
  border-radius: 999px;
  background: #e5e5ea;
  margin-top: 0.35rem;
  overflow: hidden;
}

.password-strength__bar {
  height: 100%;
  border-radius: 999px;
  transition: width 0.2s ease;
}

.password-strength__bar--weak {
  background: #d70015;
}

.password-strength__bar--mid {
  background: #ff9500;
}

.password-strength__bar--good {
  background: #34c759;
}

.password-strength__bar--strong {
  background: #0071e3;
}

.password-strength__label {
  margin: 0.25rem 0 0;
  font-size: 0.78rem;
  color: #515154;
}

.password-strength__hints {
  margin: 0.35rem 0 0;
  padding-left: 1.1rem;
  font-size: 0.78rem;
  color: #86868b;
}
</style>
